/* Dynamic Plot code copyright 2015 (c) Tuva Labs Inc. */
/**
 * created by cmiller
 */

// import {Dyn} from '../dynDataSet/dynMiscellany';
// import mediator from '../dynDataSet/mediatorUtil';

import { hasMathJaxPattern } from '../../utils/Util';

export const wrapDynMarginView = function(Dyn) {
	let mediator = Dyn.mediator;

    /** Base class for the top or right visible margins of the plot. */
    class MarginView {
        constructor() {
            this.titleFullText = ""; // top: plot title, right: attribute name
            this.isDefaultTitle = false;
        }

        destroy() {
            // for future use; nothing to do at the moment
        }

        /**
         * Set the plot title (if top margin), or legend prompt string (if right margin).
         * @param titleString - new title string (empty string is OK)
         * @param forceUpdate - (OBSOLETE)
         * @param isDefaultTitle - true if this is the default title provided by setDefaultPlotTitle()
         */
        setTitle( titleString, forceUpdate, isDefaultTitle ) {
            // #1390 restrictions on setting title moved to Dyn.PlotShaper.setDefaultPlotTitle()
            this.titleFullText = titleString;
            this.isDefaultTitle = Boolean( isDefaultTitle ); // for save()
            this.updateVisuals();
            // console.log( "setTitle()", this.isHorizontal ? "plot title:":"legend prompt:", "'"+this.titleFullText+"'", "is default", this.isDefaultTitle );
        }

        /**
         * @returns {String} - returns title string
         */
        getTitle() {
            "use strict";
            return this.titleFullText; //this.title.attr("text");
        }

        /**
         * @returns {Dyn.MarginView.legendThickness|*} -- desired view thickness (right margin:width, top margin:height) in pixels.
         */
        calcThickness() {
            "use strict";
            return Dyn.plotUtil.kMinAxisThickness; // default, can be overriden
        }

        needsNewThickness() {
            "use strict";
            var currentThickness = this.bounds.widthOrHeight( this.isHorizontal ),
                newThickness = this.calcThickness();

            return( newThickness !== currentThickness );
        }
    }
    Dyn.MarginView = MarginView;

    /***************************************
     *
     */
    class TopMarginView extends MarginView {

        constructor( w, h, plotPaper ) {
            super();
            this.bounds = new Dyn.mathUtil.Rect( 0, 0, w, h );
            this.titleRect = new Dyn.mathUtil.Rect( 0, 0, w, h );
            this.isHorizontal = true;
            this.axisID = 'top';

            // Raphael elements
            this.paper = plotPaper;
            this.title = null;
            this.subTitle = null; // NOTE: not currently in use; saved for possible future use
            this.titleEditButton = null;
            this.closebox = null;
            this.menuButton = null; // context menu button used by some plot types
            this.softResetButton = null; // optional button to reset to the initial plot state
            this.backgroundRect = null; // partially opaque when title is over a map plot, for readability

            this.excludedCasesWidth = 0; // excluded cases display which we don't want to overlap with title
        };

        /**
         * Save the top view to a data object that can be JSONified
         * @returns {{}} - data to be used by restore()
         */
        save() {
            var marginModel = {};
            // save the chart title; escape the single quote
            // don't save default title, so we can restore to a different default if language has changed #1389
            if( ! this.isDefaultTitle ) {
                marginModel.chartTitleText = this.getTitle().replace( /'/g, "\\'" );
            }
            return marginModel;
        }

        /**
         * Change top and right margin view to match the saved data.
         */
        restore( savedData ) {
            "use strict";
            if( this.hasSavedPlotTitle( savedData )) {
                // Unescape the single quote
                this.setTitle( savedData.chartTitleText.replace(/\\'/g,"'") );
            }
        }

        hasSavedPlotTitle( savedData ) {
            return (typeof savedData.chartTitleText ==='string'); // restore blank or non-blank title string
        }

        /** initialize visual elements (SVG/Raphael elements). See also .updateVisuals() and .layerVisuals() */
        initVisuals( titleString ) {
            "use strict";
            var marginView    = this,
                titleFontSize = Dyn.preferences.getFontSize( Dyn.plotUtil.kPlotTitleFontSize ),
                subTitleHeight = titleFontSize / this.kTopAndBottomMarginFraction,
                viewOnly = Dyn.preferences.isViewOnly();

            this.backgroundRect = this.paper.rect( 0,0,0,0 )
                .attr({
                    fill: Dyn.plotUtil.normalDropColor,
                    stroke: "none",
                    opacity: Dyn.plotUtil.kLegendOpacity // show map behind legend
                });

            this.titleFullText = titleString;
            this.title         = this.paper.text(
                this.bounds.centerX(),
                this.bounds.centerY(), titleString ).attr( {
                "font-size": titleFontSize,
                "font-family": Dyn.plotUtil.plotTitleFontFamily,
                "cursor": 'pointer',
                "class": 'tde-plot-title no-print',
            } );
            if( ! viewOnly ) {
                this.title.attr({
                    "cursor": 'pointer'
                });
            }
            // make title node focusable
            this.title.node.setAttribute( "focusable", "true" );
            this.title.node.setAttribute( "tabindex", "0" );
            this.title.node.setAttribute( "id", Dyn.plotUtil.plotTitleId);

            /* this.title.hover(
                function ( e ) {
                    e.currentTarget.style.cursor = Dyn.plotUtil.cursorPointer;
                }, function ( e ) {
                    e.currentTarget.style.cursor = Dyn.plotUtil.cursorDefault;
                } ); */

            this.subTitle = this.paper.text(
                this.bounds.centerX(),
                this.bounds.y + subTitleHeight, "" ).attr( {
                "font-size": Dyn.preferences.getFontSize( Dyn.plotUtil.kPlotSubtitleFontSize ),
                "class": 'tde-plot-title',
                } );
            this.subTitle.node.setAttribute("id", Dyn.plotUtil.plotSubTitleId);

            // enable inline Text Editing
            this.paper.inlineTextEditing( this.title, {type: 'text'} );

            // Create edit button
            const buttonOptions = Dyn.plotUtil.roundRectAxisButtonOptions();
            this.titleEditButton = Dyn.plotUtil.makeSquareButton(
                this.paper,
                Dyn.plotUtil.getPencilIcon( Dyn.plotUtil.getTouchAdjustedSize( Dyn.plotUtil.plotTitlePencilSize )),
                hoverIn, hoverOut, titleClick,
                false, Dyn.loc( "edit plot title"), Dyn.plotUtil.editPlotTitleButtonId, buttonOptions );
            // Set required attributes for edit button
            this.titleEditButton
                .data( 'type', "editButton" )
                .data( "tooltip", Dyn.loc( "edit plot title"))
                .attr( 'cursor', Dyn.plotUtil.cursorPointer );

            this.closebox = Dyn.plotUtil.makeCloseBox(
                this.paper, hoverIn, hoverOut, clickedOnClosebox ,
                Dyn.loc(Dyn.plotUtil.promptLegendCloseButtonAriaLabel), Dyn.plotUtil.legendViewCloseButtonId,
                buttonOptions );
            this.closebox.data( "tooltip", Dyn.loc( "remove plot title"));

            this.menuButton = Dyn.plotUtil.makeContextMenuButton(
                this.paper, clickedOnMenu,
                {}, /* keyhandler default is Enter==clickHandler */
                undefined /* aria label undefined */,
                0, // updated to the current plot by contextMenuOptions()
                buttonOptions );

            let softResetTipAndLabel = Dyn.loc( "Reload Plot State");
            this.softResetButton = Dyn.plotUtil.makeSquareButton(
                this.paper,
                Dyn.plotUtil.getSoftResetIcon( Dyn.plotUtil.getTouchAdjustedSize( Dyn.plotUtil.plotTitleSoftResetIconSize)),
                hoverIn, hoverOut, clickedOnSoftReset,
                {}, // keyhandler default is Enter==clickHandler
                softResetTipAndLabel, // aria label
                Dyn.plotUtil.softResetTitleButtonId,
                buttonOptions )
                .data( 'type', "softResetButton" )
                .data( "tooltip", softResetTipAndLabel )
                .attr( 'cursor', Dyn.plotUtil.cursorPointer );

            function hoverIn( e ) {
                let symbol = this,
                    cover = this.getCoverElement(),
                    isCloseBox = (cover === marginView.closebox.getCoverElement());
                cover.attr({ fill: Dyn.plotUtil.axisButtonBgDownColor });
                symbol.attr( { fill: Dyn.plotUtil.axisButtonDownColor,
                    stroke: isCloseBox ? Dyn.plotUtil.axisButtonDownColor : "none" } );
                if( this.data("tooltip"))
                    Dyn.toolTip.show( this, e, { x:cover.getBBox().x2, y:cover.getBBox().y2, alignLeft: true });
            }
            function hoverOut( e ) {
                let symbol = this,
                    cover = this.getCoverElement();
                cover.attr({ fill: Dyn.plotUtil.axisButtonBgUpColor });
                symbol.attr( { fill: Dyn.plotUtil.axisButtonUpColor, stroke: "none" } );
                if( Dyn.toolTip.isMyElement( this ))
                    Dyn.toolTip.hide();
            }
            function clickedOnClosebox( /*e */ ) {
                // handler to remove title from plot
                marginView.setTitle("", true );
                marginView.updateVisuals();
            }

            function clickedOnMenu( event ) {
                Dyn.plotView.plotShaper.handleContextMenuClick( event );
            }

            function clickedOnSoftReset( event ) {
                Dyn.undoManager.hasUndoSteps() && Dyn.plotView.restoreSavedPlotState();
            }

            // Start inline editing on click
            function titleClick(e) {
                e.stopPropagation();
                e.preventDefault()
                var input,
                    thisTextElement = marginView.title;

                thisTextElement.attr( "text", marginView.titleFullText ); // restore full text if ellided
                thisTextElement.data("inTextEdit",true);
                input = thisTextElement.inlineTextEditing.startEditing( {
                    color: Dyn.plotUtil.titleColor,
                    bgColor: Dyn.plotUtil.normalDropColor });
                marginView.titleEditButton.hide(); // Hide the edit button in editing mode
                marginView.closebox.hide();
                marginView.menuButton.hide();
                marginView.softResetButton.hide();
                mediator.publish( "plot:startViewMode", {  // allow plot view click to blur
                    mode: "text-editing",
                    textElement: thisTextElement,
                    stopEditing: stopEditing });

                input.addEventListener( "blur", function( e ) {
                    // Stop inline editing after blur on the text field
                    stopEditing( e );
                }, false );

                input.addEventListener( "keypress", function ( e ) {
                    // Stop inline editing after entering Return/Enter key (key code 13)
                    var key = e.which || e.keyCode;
                    if ( key === 13 ) {
                        stopEditing( e );
                    }
                }, false );

                function stopEditing( event ) {
                    thisTextElement.node.setAttribute("class", "tde-plot-title");
                    if( thisTextElement.data("inTextEdit") ) {
                        thisTextElement.data("inTextEdit",false);
                        if( event )
                            event.stopImmediatePropagation();
                        thisTextElement.inlineTextEditing.stopEditing();
                        let fullText = thisTextElement.attr( "text" );
                        if( /^[\s_]*$/.test(fullText)) {
                            fullText = ""; // replace "  ", or "_" with blank title
                            thisTextElement.attr( "text", fullText );
                        }
                        marginView.setTitle( fullText, true /* forceUpdate */ ); // save full text and ellide
                        mediator.publish( "plot:stopViewMode", {mode:"text-editing"} ); // notify plot view
                        marginView.titleEditButton.show(); // Hide the edit button in editing mode
                        // Focus back source element after editing is done when the editing triggered through keyboard
                        if(e.type === 'keydown') {
                            e.target && e.target.focus();
                        }
                    }
                }
            }

            // To set click event for edit button and title
            if( ! viewOnly ){
                this.title.click(titleClick);
                // Listen for keydown event for title
                this.title.node.onkeydown = function(e) {
                    if( e.key === "Enter" ){
                        titleClick(e);
                    }
                }
            }
        }

        /**
         * re-layer the Rafael visual elements so the correct ones are in front (z-axis).
         * designed to be compatible with Dyn.PlotView.layerVisuals
         */
        layerVisuals() {
            // Note: this sequence also affects keyboard focus order of the buttons
            if( this.backgroundRect ) {
                this.backgroundRect.toFront();
            }
            if( this.title ) {
                this.title.toFront();
            }
            if( this.subTitle ) {
                this.subTitle.toFront();
            }
            if( this.titleEditButton ) {
                this.titleEditButton.toFront();
            }
            if( this.menuButton ) {
                this.menuButton.toFront();
            }
            if( this.closebox ) {
                this.closebox.toFront();
            }
            if( this.softResetButton ) {
                this.softResetButton.toFront();
            }
        };

        setFocus() {
            if( Dyn.plotUtil.isElementVisible( this.title ) && this.titleFullText.length > 0 ) {
                this.title.node.focus(); // when title text is visible with non-empty string
            } else if( Dyn.plotUtil.isElementVisible( this.titleEditButton.getCoverElement())) {
                this.titleEditButton.getCoverElement().node.focus(); // when titleFullText===""
            }
            // else if title hidden, no elements are visible; there is nothing here to focus
        }

        /**
         * Update the top margin visual elements, e.g. on new bounding box, or new attribute.
         */
        updateVisuals( optionalViewMode ) {
            const viewMode = optionalViewMode  || new Dyn.PlotViewMode(),
                showControls = viewMode.showControls(),
                showPlotTitle = Dyn.preferences.isPlotTitleVisible(),
                showEditButton = showControls && showPlotTitle,
                showContextMenu = showControls && Dyn.plotView.plotShaper.wantPlotContextMenu(),
                showCloseBox = showControls && showPlotTitle && (this.titleFullText.length > 0),
                showSoftResetButton = showControls && Dyn.plotView.hasSavedPlotState() && ! Dyn.plotUtil.isDrawOnly(),
                numVisibleButtons = [showEditButton,showContextMenu, showCloseBox, showSoftResetButton]
                    .reduce((a, b) => a+=( b ? 1 : 0), 0);

            // update background rect for maps
            const legendBounds = Dyn.plotView.rightMargin.bounds;
            this.backgroundRect.attr( {
                x: this.bounds.x,
                y: this.bounds.y,
                width: Math.max(0, legendBounds.right() - this.bounds.left()),
                height: this.bounds.height,
        		fill: Dyn.plotUtil.normalDropColor,
        		opacity: Dyn.plotUtil.kLegendOpacity });
            Dyn.plotUtil.showOrHideElement( this.backgroundRect, showPlotTitle );

            // 1st pass: title is centered: show/hide/position
            let buttonSpacing = Dyn.plotUtil.getTouchAdjustedSize( Dyn.plotUtil.iconSize ) + Dyn.plotUtil.plotButtonSpacing,
                titleFontSize = Dyn.preferences.getFontSize( Dyn.plotUtil.kPlotTitleFontSize ),
                subTitleHeight = titleFontSize / this.kTopAndBottomMarginFraction,
                titleRect = this.titleRect.copyRect( this.bounds );
            this.title.attr({ x: titleRect.centerX(), y: titleRect.centerY(),
                text: this.titleFullText, 'font-size':titleFontSize, fill: Dyn.plotUtil.titleColor });
            Dyn.plotUtil.showOrHideElement( this.title, showPlotTitle );

            // 2nd pass: clip title and move text to available space in title bounds?
            const firstButtonOffset = 2; // because otherwise it is too close to title
            let bbox = Dyn.plotUtil.clipStringToWidth( this.title, titleRect.width ),
                freeSpaceToRight = (titleRect.width - bbox.width)/2,
                buttonAndExcludedCasesWidth = (buttonSpacing * numVisibleButtons) + this.excludedCasesWidth,
                buttonCenterX, buttonCenterY = titleRect.centerY();
            if( (this.titleFullText.length===0) || !showPlotTitle ) {
                // no title, but we may have buttons to show
                titleRect.width = buttonSpacing * numVisibleButtons;
                titleRect.x = this.bounds.centerX() - titleRect.width/2;
                buttonCenterX = titleRect.x + buttonSpacing/2;
            } else {
                if( freeSpaceToRight < buttonAndExcludedCasesWidth ) {
                    // full title overlaps with buttons and excluded-cases display; clip+position title again to fit
                    titleRect.width = Math.max( 0, this.bounds.width - buttonAndExcludedCasesWidth );
                    this.title.attr({ x: titleRect.centerX(), text: this.titleFullText });
                    bbox = Dyn.plotUtil.clipStringToWidth( this.title, titleRect.width );
                    titleRect.x = bbox.x;
                    titleRect.width = bbox.width + (buttonSpacing * numVisibleButtons) + firstButtonOffset;
                    buttonCenterX = bbox.x2 + buttonSpacing/2 + firstButtonOffset;
                } else {
                    // full title fits; keep it centered over plot
                    titleRect.x = bbox.x;
                    titleRect.width = bbox.width;
                    buttonCenterX = bbox.x2 + buttonSpacing/2 + firstButtonOffset;
                }
            }
            this.titleRect.addDebugRect( this.paper, "green" );

            // edit button: show/hide/position to right of title
            this.titleEditButton.transform( "t" + buttonCenterX + "," + buttonCenterY );
            Dyn.plotUtil.showOrHideElement( this.titleEditButton, showEditButton );
            if( showEditButton ) buttonCenterX += buttonSpacing;

            // context menu button: show/hide/position to right of previous button
            this.menuButton.transform( "t" + buttonCenterX + "," + buttonCenterY );
            Dyn.plotUtil.showOrHideElement( this.menuButton, showContextMenu );
            if( showContextMenu ) buttonCenterX += buttonSpacing;

            // closebox: show/hide/position to right of previous button
            this.closebox.transform( "t" + buttonCenterX + "," + buttonCenterY );
            Dyn.plotUtil.showOrHideElement( this.closebox, showCloseBox );
            if( showCloseBox ) buttonCenterX += buttonSpacing;

            // soft reset button: show/hide/position to right of previous button
            this.softResetButton.transform( "t" + buttonCenterX + "," + buttonCenterY );
            Dyn.plotUtil.showOrHideElement( this.softResetButton, showSoftResetButton );
            if( showSoftResetButton ) buttonCenterX += buttonSpacing;

            // update tooltips for current language
            this.titleEditButton.data( 'tooltip', Dyn.loc( "edit plot title"));
            this.closebox.data( 'tooltip', Dyn.loc( "remove plot title"));

            // subtitle (currently unused)
            this.subTitle.attr({ x: titleRect.centerX(), y:this.bounds.y + subTitleHeight, "font-size": Dyn.preferences.getFontSize(  Dyn.plotUtil.kPlotSubtitleFontSize ) });
        }

        /**
         * set or clear image-ready or view-only mode
         * @param mode ({Dyn.PlotViewMode}|{Dyn.PlotModeImageReady}) - new mode settings
         */
        setViewMode( mode ) {
            this.updateVisuals( mode );
        }

        /**
         * @param {number} w - width of excluded cases display
         */
        setExcludedCasesWidth( w ) {
            "use strict";
            //console.log( "setExcludedCasesWidth() ", w );
            this.excludedCasesWidth = w;
            this.updateVisuals(); // we may need to reposition title
        }

        /** Show or hide the plot context menu in title? */
        showHidePlotMenu( wantPlotContextMenu ) {
            let wantMenu = wantPlotContextMenu && ! Dyn.preferences.isViewOnly(),
                menuVisible = this.menuButton && Dyn.plotUtil.isElementVisible( this.menuButton );
            if( wantMenu ) {
                // update the context menu for the current plot type
                let options = Dyn.plotView.plotShaper.contextMenuOptions(),
                    cover = this.menuButton.getCoverElement(),
                    symbol = this.menuButton.getSymbolElement();
                Dyn.assert( options?.menuID && options?.menuToolTip );
                cover.node.setAttribute( "id", options?.menuID );
                cover.node.setAttribute( "aria-label", options?.menuToolTip );
                symbol.data( "tooltip", options?.menuToolTip );
            }
            if( wantMenu !== menuVisible) {
                this.layerVisuals(); // show and adjust title and all buttons to accommodate .menuButton
            }
        }

        /** show or hide the subTitle, expanding the size of the top margin */
        showHideSubtitle( wantVisible ) {
            "use strict";

            if( !wantVisible ) {
                this.resetSubtitle();
            }
            this.bounds.height = this.calcThickness();
        }

        /** show or hide the subtitle, expanding the size of the top margin */
        setSubtitle( newSubTitleString ) {
            "use strict";
            //set the metadata in subtitle
            this.subTitle.attr("text", newSubTitleString);
            this.subTitle.attr({"transform": Dyn.plotUtil.getTextTransform( this.subTitle, "center,top" )});
        }

        // reset the subtitle
        resetSubtitle() {
            "use strict";
            this.subTitle.attr("text", "");
        }

        /**
         * Calculate the desired Top Margin view thickness, so we can
         * determine if our plot view needs to be updated.
         * NOTE: does not actually update the plot view.
         */
        calcThickness() {
            let titleFontSize = Dyn.preferences.getFontSize( Dyn.plotUtil.kPlotTitleFontSize ),
                subTitleFontSize = Dyn.preferences.getFontSize( Dyn.plotUtil.kPlotSubtitleFontSize ),
                hasSubtitle = this.subTitle ? (this.subTitle.attr("text").length > 0) : false,
                newThickness = (titleFontSize + (hasSubtitle ? subTitleFontSize : 0 )) / this.kTopAndBottomMarginFraction;
            if( Dyn.plotUtil.isDrawOnly() && ! Dyn.preferences.isPlotTitleVisible()) {
                newThickness = 0; // reclaim title space only if in Draw-Only mode; otherwise we may need the space for icons
            }
            return newThickness;
        }

        setPlotSpecificOptions( options ) {
            // no top margin (plot title) options so far
        }

        /**
         * update our view after changes
         * from Dyn.PlotElement.CalcAxis() or Dyn.PlotShaper.updateAxes()
         */
        postCalcAxisUpdate() {
            "use strict";
            //var plotView = Dyn.plotView;
            // hide the subtitle if we removed an element that previously showed the subtitle.
            //if( ! plotView.plotShaper.hasElement( Dyn.EPlotControl.kControlsMargins )) {
            //	this.showHideSubtitle( false /* hide */ );
            //}
        }

    } // class TopMarginView
    Dyn.TopMarginView = TopMarginView;
    //Dyn.TopMarginView.inheritsFrom( Dyn.MarginView );
    //Dyn.TopMarginView.prototype.kExcludedCasesMaxWhiteSpaceFraction = 0.5; // want excluded cases display to be 50% of whitespace around title or less
    Dyn.TopMarginView.prototype.kTopAndBottomMarginFraction = 0.5; // want top and bottom margin to be 50% of total TopMarginView height


    /************************************************
     * Dyn.RightMarginView()
     *
     * The Right Margin of the plot is the area displaying the Legend and/or right-hand gutter of plot.
     * NOTE: set Dyn.debugViewRects = true to show visual boundaries
     */
    class RightMarginView extends MarginView {

        constructor( w, h, plotPaper ) {
            super();
            this.bounds = new Dyn.mathUtil.Rect( 0, 0, w, h );
            this.isHorizontal = false;
            this.isUsingPromptTitle = ! Dyn.plotUtil.isDrawOnly();
            this.dragData = {};
            this.needsLegendUpdate = true;
            this.isUsingPlusMinus = false;
            this.isUsingNumericIntervals = false;
            this.attribute = null;
            this.axisID = 'right';

            // sizes
            this.legendFontSize = 14;
            this.legendColorSquare = 10;
            this.legendNumericTickLength = 3;
            this.legendMinorTickLength = 1;
            this.showMinorTickMarks = false;
            this.legendNumericTickToTextSpace = 7;
            this.minNumTicks = 3;
            this.pixelsPerTick = 70; // tick mark approx. every x pixels
            this.spacer = 5; // white space around visible elements
            this.dragLineWidth = 7; // width of invisible drag area around boundaryLine
            this.legendThickness = Dyn.plotUtil.kLegendStdThickness; // variable view width when an attribute is displayed
            //this.nonLegendThickness = Dyn.plotUtil.kMinAxisThickness; // variable view width when no attribute/no legend

            // Raphael elements
            this.paper = plotPaper;
            this.title = null;
            this.closebox = null;
            this.scrollbar = null; // hidden unless list of categories is long
            this.boundaryLine = null;
            this.dragLine = null;
            this.plusButton = null;
            this.minusButton = null;
            this.dropRect = null;
            this.dropRectOverlay = null;
            this.glowElement = null;
            this.gradientOverlay = null; // invisible drag area over numeric gradient
            this.marqueeSelectRect = null; // visible only if numeric selection over gradient
            this.attributeLegend = null;
        }

        // #2139 as of v3.1.1 thickness of right margin with no legend is now constant
        get nonLegendThickness() { return Dyn.plotUtil.kMinAxisThickness }

        save() {
            let marginModel = {
                legendThickness: this.legendThickness,
                // nonLegendThickness: this.nonLegendThickness // removed for #2139
            };
            if( this.isUsingPromptTitle ) {
                marginModel.isUsingPromptTitle = this.isUsingPromptTitle;
            }
            return marginModel;
        }

        restore( savedData ) {
            // #1415 restore legendThickness
            if( Number.isFinite( savedData.legendThickness )) {
                this.legendThickness = savedData.legendThickness;
            }
            // #2139 as of v3.1.1 thickness of right margin with no legend is now constant;
            //      old parameters savedData.nonLegendThickness are ignored.
            // #1335 clear default "PromptTitle" on restore unless savedData.isUsingPromptTitle === true
            this.isUsingPromptTitle = Boolean( savedData.isUsingPromptTitle && ! Dyn.plotUtil.isDrawOnly() );
        }

        initVisuals( titleString ) {

            function showDragLine() {
                return Boolean(marginView.attribute); // only if there is a visible legend for an attribute #2139
            }

            function lineHoverIn( e ) {
                if( showDragLine()) {
                    this.data( 'inHover', true );
                    e.currentTarget.style.cursor = Dyn.plotUtil.cursorHorizontalBothSide;
                    this.attr( { stroke: marginView.legendCoverHighlightColor } );
                    marginView.showHideBoundaryLine();
                }
            }
            function lineHoverOut( e ) {
                this.removeData( 'inHover' );
                e.currentTarget.style.cursor = Dyn.plotUtil.cursorDefault;
                if( ! this.data('inDrag')) {
                    this.attr({stroke:marginView.legendCoverColor});
                    marginView.showHideBoundaryLine();
                }
            }
            function movedLine( dx /*,dy, x, y, e*/ ) {
                if( showDragLine()) {
                    let oldWidth = this.data( 'width' ),
                        newWidth = oldWidth - dx,
                        marginData = {
                            "isHorizontal": (marginView.isHorizontal),
                            oldWidth: oldWidth,
                            newWidth: newWidth
                        };
                    // tell plot view to update (this.postCalcAxisUpdate() calls calcThickness())
                    marginView.legendThickness = newWidth;
                    mediator.publish( "marginView:dragBoundary", marginData );
                }
            }
            function startDragLine( /*x, y, e */ ) {
                if( showDragLine()) {
                    this.data( 'inDrag', true );
                    this.data( 'width', marginView.bounds.width );
                    this.attr( { stroke: marginView.legendCoverHighlightColor } );
                    marginView.showHideBoundaryLine();
                }
            }
            function endDragLine( /*e */ ) {
                if( showDragLine()) {
                    this.removeData( 'inDrag' );
                    this.removeData( 'width' );
                    if( !this.data( 'inHover' ) ) {
                        this.attr( { stroke: marginView.legendCoverColor } );
                        marginView.showHideBoundaryLine();
                    }
                    Dyn.plotUtil.setAsKeyboardFocus( this );
                }
            }
            function hoverInClosebox( e ) {
                let cover = marginView.closebox.getCoverElement();
                cover.attr({ fill: Dyn.plotUtil.axisButtonBgDownColor });
                this.attr( { fill: Dyn.plotUtil.axisButtonDownColor, stroke: Dyn.plotUtil.axisButtonDownColor } );
                let bbox = this.getCoverElement().getBBox();
                if( this.data("tooltip"))
                    Dyn.toolTip.show( this, e, { x:bbox.x, y:bbox.y2, alignRight: true });
            }
            function hoverOutClosebox( e ) {
                let cover = marginView.closebox.getCoverElement();
                cover.attr({ fill: Dyn.plotUtil.axisButtonBgUpColor });
                this.attr( { fill: Dyn.plotUtil.axisButtonUpColor, stroke: "none" } );
                if( Dyn.toolTip.isMyElement( this ))
                    Dyn.toolTip.hide();
            }
            function clickedOnClosebox( /*e */ ) {
                // handler to remove attribute or prompt on click title
                marginView.isUsingPromptTitle = false;
                if( marginView.attribute ) {
                    var attributeData = { id: "", name: "", object: "plot" };
                    mediator.publish( "records:attributeNameSelected", attributeData );
                } else {
                    marginView.updateVisuals();
                }
            }
            function keyDownHandler( e ) {
                //console.log( "keyDownHandler keydown:", e.key );
                switch( e.key ) { // const {key} = event; ES6+
                case "Delete":
                case "Backspace":
                    clickedOnClosebox();
                    break;
                //case "Enter":
                //    break;
                case "ArrowLeft":
                case "ArrowRight":
                case "ArrowUp":
                case "ArrowDown": if( showDragLine() ) {   // move this element one pixel in arrow's direction
                    let dist = Dyn.plotUtil.arrowKeyAcceleratedDistance( e.key ),
                        dragLine = this;
                    startDragLine.call( dragLine, e );
                    movedLine.call( dragLine, dist.dx, dist.dy, undefined, undefined, e );
                    endDragLine.call( dragLine, e );
                }
                    break;
                }
            }

            function makeNumericSelectionRange( view, y1, y2, event, element ) {
                // create or update the visible marquee selection of the numeric gradient, from mouse position
                let dd = view.dragData,
                    referenceElement = view.paper.bottom, // works just like Dyn.plotView.backgroundRect
                    py1 = Dyn.plotUtil.dragEventXYtoPaperXY( 0, y1, event, referenceElement ).y,
                    py2 = Dyn.plotUtil.dragEventXYtoPaperXY( 0, y2, event, referenceElement ).y;
                dd.viewRange = (new Dyn.mathUtil.Range( py1, py2)).normalize();
                dd.dataRange = (new Dyn.mathUtil.Range(
                        dd.coordMap.inverseTransform( dd.viewRange.min ),
                        dd.coordMap.inverseTransform( dd.viewRange.max ))).normalize();
                view.showMarqueeSelectRect( dd.viewRange.min, dd.viewRange.max );
                // update all views with new case selection, if any, including this view.updateVisuals()
                mediator.publish( "records:casesSelectedByNumericRange", dd );
            }
            function dragGradientMove( dx, dy, x, y, event ) {
                makeNumericSelectionRange( marginView, y-dy, y, event, this );
                Dyn.plotUtil.safeStopPropogation( event );
            }
            function dragGradientStart( x, y, event ) {
                marginView.dragData.attributeID = marginView.getColorAttribute().id;
                makeNumericSelectionRange( marginView, y, y, event, this );
                Dyn.plotUtil.safeStopPropogation( event );
            }
            function dragGradientEnd( /*e */ ) {
                Dyn.plotUtil.safeStopPropogation( event );
            }
            function dragGradientHoverIn() {
                Dyn.plotView.plotShaper.prepareElementsForDrag( true );
            }
            function dragGradientHoverOut() {
                Dyn.plotView.plotShaper.prepareElementsForDrag( false );
            }

            var marginView = this;

            // init right margin
            this.dropRect = this.paper.rect( this.bounds.x, this.bounds.y, this.bounds.width, this.bounds.height, 0 )
                .attr( {
                    fill: this.getDropRectFillColor(),
                    stroke: Dyn.plotUtil.promptDropStrokeColor,
                    'stroke-width': Dyn.plotUtil.promptDropRectStrokeWidth,
                    'stroke-opacity': this.getDropRectStrokeOpacity(),
                    opacity: this.getDropRectFillOpacity()
                });
            this.dropRect.node.setAttribute("id", Dyn.plotUtil.legendViewAreaId);
            this.dropRect.node.setAttribute("class", Dyn.plotUtil.hideOnPreviewClass);

            this.scrollbar = new Dyn.PlotScrollbar( this.paper, false /*vertical orientation*/,
                                function() { marginView.updateVisuals(); } );

            // NOTE: plot view now updates the margin view, so it does not need to listen directly
            //mediator.subscribe( "records:setSelectedAttribute", this.handleSelectedAttribute );
            // specify the drop rect
            // add the listener to clear the rect, and based on the type of the attribute selected
            // either gradient or categories.

            // vertical line inset from left edge of right margin
            let lx = this.bounds.x+this.spacer,
                ly1 = this.bounds.y+this.spacer,
                ly2 = ly1 + this.bounds.height-(this.spacer*2);

            this.boundaryLine = this.paper.
                path( "M"+lx+","+ly1+"L"+lx+","+ly2 ).
                attr({stroke:this.legendBorderColor} ).
                hide(); // hide until we have a selected attribute
            this.dragLine = this.paper.
                path( "M"+lx+","+ly1+"L"+lx+","+ly2 ).
                attr({stroke:this.legendCoverColor, "stroke-width":Dyn.plotUtil.getTouchAdjustedSize( this.dragLineWidth ), "stroke-opacity":this.legendCoverOpacity } ).
                hover( lineHoverIn, lineHoverOut ).
                drag( movedLine, startDragLine, endDragLine ).
                hide(); // hide until we have a selected attribute
            Dyn.plotUtil.setKeyboardFocusable( this.dragLine, keyDownHandler.bind( this.dragLine ));

            const buttonSize = Dyn.plotUtil.getTouchAdjustedSize( Dyn.plotUtil.iconSize ),
                buttonOptions = Dyn.plotUtil.roundRectAxisButtonOptions();
            this.closebox = Dyn.plotUtil.makeCloseBox(
                    this.paper, hoverInClosebox, hoverOutClosebox, clickedOnClosebox ,
                    Dyn.loc(Dyn.plotUtil.promptLegendCloseButtonAriaLabel), Dyn.plotUtil.yAxisCloseButtonRightId,
                buttonOptions );
            this.plusButton  = Dyn.axisUtil.makePlusMinusButton( this, "plusButton",
                Dyn.loc("increase number of bins"), Dyn.plotUtil.legendPlusButtonId );
            this.minusButton = Dyn.axisUtil.makePlusMinusButton( this, "minusButton",
                Dyn.loc("decrease number of bins"), Dyn.plotUtil.legendMinusButtonId );
            this.marqueeSelectRect = this.paper.rect( 0, 0, 0, 0 ).
                attr({ fill:"none", stroke:"#000" /*, "stroke-dasharray":"5,5"*/} ).hide();

            // Overlay element to listen events
            this.gradientOverlay = this.paper.rect( 0, 0, 0, 0 )
                .attr({ fill:"white" , stroke: "blue",
                    opacity: Dyn.plotUtil.opacityInvisibleButDraggable,
                    cursor:Dyn.plotUtil.cursorPointer})
                .drag( dragGradientMove, dragGradientStart, dragGradientEnd )
                .hide();
            this.dropRectOverlay = this.paper.rect( this.bounds.x, this.bounds.y, this.bounds.width, this.bounds.height, 0 )
                .attr({ fill:"white" , stroke: "none", opacity: Dyn.plotUtil.opacityInvisibleButDraggable })
                .hide();
            if(!Dyn.environment.isTouchDevice()) {
                this.dropRectOverlay.mouseover(function() {
                    marginView.mouseOverDroppableAreas();
                });
                //this.dropRectOverlay.touchstart(function() {
                //    marginView.mouseOverDroppableAreas();
                //});
                // To reset the mouse over style when attribute is in drag mode
                this.dropRectOverlay.mouseout(function() {
                    marginView.mouseOutDroppableAreas();
                });
                // To handle the dropped attribute
                this.dropRectOverlay.mouseup(function() {
                    marginView.mouseUpHandler();
                });
                this.gradientOverlay.hover( dragGradientHoverIn, dragGradientHoverOut );
            }

            Dyn.addSubcribersToClean(
                mediator.subscribe( "records:setCasesSelected", (data) => {
                    this.handleCasesSelected(data); }));
        }

        setFocus() {
            let firstVisibleElement = [
                    this.minusButton.getCoverElement(),
                    this.plusButton.getCoverElement(),
                    this.closebox.getCoverElement(),
                    this.dragLine ].find((e) => Dyn.plotUtil.isElementVisible(e));
            firstVisibleElement?.node.focus();
        }

        /**
         * Layer the graphical elements from back to front (z-axis)
         */
        layerVisuals() {
            if( this.dropRect ) {
                this.dropRect.toFront();
            }
            if( this.title ) {
                this.title.toFront();
            }
            if( this.minusButton ) { // keyboard focus element 1, if not hidden
                this.minusButton.toFront();
            }
            if( this.plusButton ) { // keyboard focus element 2, if not hidden
                this.plusButton.toFront();
            }
            if( this.closebox ) { // keyboard focus element 3, if not hidden
                this.closebox.toFront();
            }
            if( this.boundaryLine ) {
                this.boundaryLine.toFront();
            }
            if( this.dragLine ) { // keyboard focus element 4, if not hidden
                this.dragLine.toFront();
            }
            if( this.attributeLegend ) {
                this.attributeLegend.toFront();
            }
            this.gradientOverlay.toFront();
            this.marqueeSelectRect.toFront();
            this.scrollbar.toFront();
            if( this.draggedAttr ) {
                // overlay rect on top, hidden or (shown and opacityInvisibleButDraggable)
                this.dropRectOverlay.toFront();
            }
        }

        /**
         * Given a new bounding box, or new attribute, update the visual elements to match.
         */
        updateVisuals() {
            "use strict";

            // update the boundaryLine position
            let viewOnly = Dyn.preferences.isViewOnly(),
                buttonSize = Dyn.plotUtil.getTouchAdjustedSize( Dyn.plotUtil.iconSize ),
                lx     = this.bounds.x + this.spacer,
                ly1    = this.bounds.y + buttonSize,
                ly2    = ly1 + this.bounds.height - (buttonSize + this.spacer),
                bx     = lx + 2,
                by     = this.bounds.y + (0.5 * buttonSize) - 2,
                plusy  = by,
                plusx  = bx - (buttonSize+Dyn.plotUtil.plotButtonSpacing),
                minusx = plusx - (buttonSize+Dyn.plotUtil.plotButtonSpacing),
                attribute = Dyn.dataSet.getSelectedAttribute();

            var pathArray   = this.boundaryLine.attr( "path" );
            pathArray[0][1] = lx;
            pathArray[0][2] = ly1;
            pathArray[1][1] = lx;
            pathArray[1][2] = ly2;
            this.boundaryLine.attr( "path", pathArray );
            this.dragLine.attr( "path", pathArray );
            this.showHideBoundaryLine();

            // Remove title if it exists
            if( this.title ) {
                this.title.remove();
                this.title = null;
            }

            // hide, draw, or update the legend
            // NOTE: do this first so dropRect colors reflect current attribute
            Dyn.assert( attribute === this.attribute );
            this.updateLegend();

            // update dropRect
            this.dropRect.attr({
                x: this.bounds.x,
                y: this.bounds.y,
                width: this.bounds.width,
                height: this.getAdjustedHeight(),
                fill: this.getDropRectFillColor(),
                stroke: this.isUsingPromptTitle? Dyn.plotUtil.promptDropStrokeColor: Dyn.plotUtil.normalDropColor,
                'stroke-width': Dyn.plotUtil.promptDropRectStrokeWidth,
                'stroke-opacity': this.getDropRectStrokeOpacity(),
                opacity: this.getDropRectFillOpacity()
            });

            this.closebox.data( "tooltip", Dyn.loc( "remove {{title}}", {
                title: (this.attribute ? this.attribute.name : Dyn.loc(Dyn.plotStrings.promptLegend))
            }));
            // update tooltips for current language
            this.plusButton.data( "tooltip", Dyn.loc("increase number of bins"));
            this.minusButton.data( "tooltip", Dyn.loc("decrease number of bins"));

            this.closebox.transform( "t" + bx + "," + by );
            this.plusButton.transform( "t"+plusx+","+plusy );
            this.minusButton.transform( "t"+minusx+","+plusy );
            Dyn.axisUtil.updateAxisButton( this.plusButton );
            Dyn.axisUtil.updateAxisButton( this.minusButton );

            if( (!this.attribute) && this.isUsingPromptTitle ) {
                // When there are no attribute
                // Force recreate title since the paragraph library doesn't reposition when x and y attributes are changed
                this.title = this.createTitle( Dyn.plotUtil.axisButtonSpacing + buttonSize );
                let promptBBox = this.title.getBBox(Dyn.plotUtil.textBBoxAfterTransform);
                this.closebox.transform( "t" + promptBBox.cx + "," + (promptBBox.y - Dyn.plotUtil.axisButtonSpacing - buttonSize/2) );
            }
            Dyn.plotUtil.showOrHideElement( this.closebox, (this.attribute || this.isUsingPromptTitle) && !viewOnly );

            let mode = new Dyn.PlotViewMode();
            if( !(mode.showControls() && mode.showAxes())) {
                this.setViewMode( mode ); // hide controls
            }
            this.needsVisualsUpdate = false;
        }

        /**
         * Make right margin image ready or view only
         * @param mode ({Dyn.PlotViewMode}|{Dyn.PlotModeImageReady}) - new mode settings
         */
        setViewMode( mode ) {
            const showControls = mode.showControls(),
                showAxes = mode.showAxes();
            if( this.getAttribute() || this.isUsingPromptTitle ){
                Dyn.plotUtil.showOrHideElement( this.closebox, showControls );
                if( this.isUsingPromptTitle ) {
                    Dyn.plotUtil.showOrHideElement( this.dropRect, showControls );
                    Dyn.plotUtil.showOrHideElement( this.title, showControls );
                }
                if( this.isUsingPlusMinus ) {
                    Dyn.plotUtil.showOrHideElement( this.plusButton, showControls );
                    Dyn.plotUtil.showOrHideElement( this.minusButton, showControls );
                }
            }
            if(showControls) {
                Dyn.plotUtil.showOrHideElement( this.dragLine, showAxes );
                Dyn.plotUtil.showOrHideElement( this.dropRect, showAxes );
                Dyn.plotUtil.showOrHideElement( this.dropRectOverlay, showAxes );
            }
        }

        /**
         * Calculate the desired Right Margin view thickness, so we can
         * determine if our plot view needs to be updated.
         * NOTE: does not actually update the plot view.
         * @returns {Dyn.MarginView.legendThickness|*} -- desired view width in pixels.
         */
        calcThickness() {
            const hasSelectedAttribute = Boolean( this.attribute ), //Dyn.dataSet.getSelectedAttribute() ),
                minThickness = Dyn.plotUtil.kMinAxisThickness,
                maxThickness = this.paper.width * Dyn.plotUtil.kLegendMaxProportion;
            let newThickness = ( hasSelectedAttribute ?
                this.legendThickness : // user-adjusted thickness if legend visible
                this.nonLegendThickness ); // thickness if no legend (see #2139)
            if( Dyn.plotUtil.isDrawOnly() ) {
                newThickness = 0; // hide margin/legend if in draw-only mode
            } else {
                newThickness = Dyn.mathUtil.clipToRange( newThickness, minThickness, maxThickness );
                if( this.isUsingPromptTitle ) {
                    const promptFontSize = Dyn.preferences.getFontSize( Dyn.plotUtil.kPlotTitleFontSize ),
                        buttonHeight = Dyn.plotUtil.getTouchAdjustedSize( Dyn.plotUtil.iconSize ),
                        textHeight = Dyn.plotUtil.getTextBBox( this.paper, "Xy", promptFontSize ).height,
                        promptXHeight = Dyn.plotUtil.kAxisTitleMargin + Math.max( textHeight, buttonHeight );
                    newThickness = Math.max( newThickness, promptXHeight );
                }
            }
            return newThickness;
        }

        /**
         * Get the attribute used in this view, or null if none.
         * @returns {Dyn.Attribute|null}
         */
        getAttribute() {
            return this.attribute;
        }

        // Get the height of the DropRect, based on this view's height
        getAdjustedHeight() {
            if( this.isColorAxis() ){
                // end drop zone at bottom of X axis, for increased coverage of long lists of categories
                return this.bounds.height;
            } else {
                // end drop zone at top of X axis
                return this.bounds.height - (this.bounds.bottom() - Dyn.plotView.xAxis.dropRect.attr("y"));
            }
        }

        getDropRectFillColor( dropState ) {
            Dyn.DropState.isValid( dropState );
            if( this.isIncompleteAxis() ) {
                return Dyn.plotUtil.warningDropFillColor;
            } else if( dropState==="isHighlight" ){
                    return Dyn.plotUtil.highlightDropFillColor;
            } else if(this.attribute || ! this.isUsingPromptTitle){
                return Dyn.plotUtil.normalDropColor;
            } else {
                return Dyn.plotUtil.promptDropFillColor;
            }
        }

        getDropRectFillOpacity( /* dropState */ ) {
            // Dyn.DropState.isValid( dropState );
            var opacity = 1;
            if( this.draggedAttr || (this.isUsingPromptTitle && ! this.attribute) ) {
                opacity = 1; // obscure background when dragging attributes or showing prompt
            } else if( this.attribute ) {
                opacity = Dyn.plotUtil.kLegendOpacity; // show map behind legend
            } else {
                opacity = 0; // invisible if no legend or prompt, especially for map plot
            }
            return opacity;
        }

        getDropRectStrokeOpacity( /* dropState */) {
            // Dyn.DropState.isValid( dropState );
            if( this.draggedAttr || this.isUsingPromptTitle ) {
                // show stroke with prompt axis or droppable attribute over drop area
                return 1;
            } else {
                // hide stroke if axis in use, or empty, or this.isIncompleteAxis()
                return 0;
            }
        }

        /**
         * Adjust the margin/legend/dropzone for the start of a drag
         * Compare to AxisView.highlightDropAreas(), and resetHighlightDropAreas()
         * @param draggedAttr
         */
        highlightDropAreas( draggedAttr, /*deltaThickness*/ ) {
            var isStartOfDrag = Boolean( draggedAttr ), // or !this.dropRect.data( "isDragExpanded" )
                isInDrag = Boolean( this.draggedAttr ),
                fillColor = this.getDropRectFillColor("isHighlight");

            if( Dyn.preferences.isViewOnly()) {
                // no dragging/highlighting allowed
                this.dropRectOverlay.hide();
            } else if( isStartOfDrag ) {
                this.draggedAttr = draggedAttr;
                let oldWidth = this.dropRect.attr( "width" ),
                    newWidth = Math.max( this.bounds.width, Dyn.plotUtil.dropExpandMin ),
                    deltaWidth = (newWidth-oldWidth),
                    newX = this.bounds.right() - newWidth,
                    // note: this accounts for new X-axis height, assuming it was adjusted before now
                    adjustedHeight = this.getAdjustedHeight();

                this.boundaryLine.hide();
                this.dragLine.hide();
                // position and show the drop rect overlay
                this.dropRectOverlay.attr({
                    x: newX,
                    y: this.bounds.y,
                    width: newWidth,
                    height: adjustedHeight
                });
                this.dropRectOverlay.show();
                // color and expand drop area
                this.dropRect.attr({
                    x: newX,
                    y: this.bounds.y,
                    width: newWidth - 1, // -1 to show full 2 pixel border
                    height: adjustedHeight,
                    fill: fillColor,
                    stroke: Dyn.plotUtil.highlightDropStrokeColor,
                    'stroke-width': Dyn.plotUtil.highlightDropRectStrokeWidth,
                    'stroke-opacity': 1,
                    opacity: 1 //this.getDropRectStrokeOpacity()
                });
                // shift title and associated closebox to center of drop zone
                if( this.isUsingPromptTitle ) {
                    let bbox = this.title.getBBox(Dyn.plotUtil.textBBoxAfterTransform);
                    Dyn.axisUtil.shiftTitle( this.title, bbox, -deltaWidth / 2, 0, true );
                    Dyn.axisUtil.shiftButton( this.closebox, -deltaWidth / 2, 0 );
                }
                this.dropRect.data( "isDragExpanded", true );
            } else if ( isInDrag ) {
                this.dropRect.attr({
                    fill: fillColor
                });
            }
        }

        resetHighlightDropAreas() {
            if( this.draggedAttr ) {
                // undo highlightDropAreas() customizations
                this.draggedAttr = null;
                this.dropRectOverlay.hide();
                this.removeGlow();
                this.updateVisuals();
            }
            this.dropRect.data( "isDragExpanded", false );
        }

        mouseUpHandler() {
            var attr = this.draggedAttr;

            this.removeGlow();
            if( attr ) {
                var attributeData = {
                    id: attr.id,
                    name: attr.name,
                    object: "cards"
                };
                // Call the method to notify others that attribute is selected.
                mediator.publish( "records:attributeNameSelected", attributeData );
            }
        }

        mouseOverDroppableAreas( /*dropState*/ ) {
            //Dyn.DropState.isValid( dropState );
            if( this.draggedAttr && this.dropRect ) {
                this.addGlow( this.dropRect );
                this.dropRect.attr( { fill: Dyn.plotUtil.overDropFillColor } );
            }
        }

        mouseOutDroppableAreas() {
            this.removeGlow();
            this.highlightDropAreas(); // undo mouseOverDroppableAreas() styling
        }

        isPointInDropRect( xPosition, yPosition ){
            let bodyRect = document.body.getBoundingClientRect(),
                elemRect = this.paper.canvas.getBoundingClientRect(),
                offsetY = elemRect.top - bodyRect.top,
                offsetX = elemRect.left - bodyRect.left,
                isPointInRect = false;
            if( this.dropRect ) {
                let bbox = this.dropRect._getBBox(),
                    rect = (new Dyn.mathUtil.Rect( bbox )).offset( offsetX, offsetY );
                isPointInRect = rect.isPointInRect( xPosition, yPosition );
            }
            return { inDropRect:isPointInRect };
        }

        addGlow( elementToGlow ) {
            "use strict";
            var width = Dyn.plotUtil.dropGlowWidth,
                glowColor = elementToGlow.attr("stroke");
            this.removeGlow(); // remove previous glow, if any
            this.glowElement = elementToGlow.glow( {
                color: glowColor,
                width: width
            });
        }

        /**
         * Remove the glow element created by addGlow()
         */
        removeGlow() {
            "use strict";
            if( this.glowElement ) {
                this.glowElement.remove();
                this.glowElement = null;
            }
        }

        /**
         * Create title
         */
        createTitle( buttonWidth ) {
            const bbox = new Dyn.mathUtil.Rect( this.dropRect._getBBox()),
                titleFontSize = Dyn.preferences.getFontSize( Dyn.plotUtil.kPlotTitleFontSize ),
                textStyle = {
                    "font-size": titleFontSize,
                    "font-family": Dyn.plotUtil.kAxisFontFamily,
                    "text-anchor": "middle",
                    'fill': Dyn.plotUtil.titleColor,
                    "class": (this.isIncompleteAxis() ? 'tde-attribute-name tde-alert' : 'tde-attribute-name') + " " + Dyn.plotUtil.hideOnPreviewClass
                };

            // vertical text centered, for a narrow drop rect
            let title = this.paper.text( bbox.centerX(), bbox.centerY() + buttonWidth/2,
                Dyn.loc( Dyn.plotUtil.isTuvaJr() ? Dyn.plotStrings.promptJr : Dyn.plotStrings.promptLegend ))
                .attr(textStyle);
            title.attr( "transform", "r-90" ); // rotate last to work around bug #21 (rotated text disappears randomly)?
            Dyn.plotUtil.clipStringToWidth( title, Math.max(0, bbox.height - buttonWidth ));
            return title;

            // wrapped horizontal text paragraph, for a wide drop rect
            // const lineHeight = titleFontSize * 1.35; // 135%, ref: https://practicaltypography.com/line-spacing.html;
            // return this.paper.paragraph( {
            //     x: Math.round( bbox.x + bbox.width / 2 ),
            //     y: Math.round( bbox.y + bbox.height / 2 - 2 * lineHeight ),
            //     maxWidth: 45,
            //     lineHeight: lineHeight,
            //     text: Dyn.loc( Dyn.plotUtil.isTuvaJr() ? Dyn.plotStrings.promptJr : Dyn.plotStrings.promptLegend ),
            //     textStyle: textStyle
            // } );
        }

        // To reset to default view
        reset() {
            this.setSelectedAttributeLegend( null );
            this.legendThickness = Dyn.plotUtil.kLegendStdThickness;
            // this.nonLegendThickness = Dyn.plotUtil.kMinAxisThickness;
            this.isUsingPromptTitle = ! Dyn.plotUtil.isDrawOnly();
            this.isIncompleteAxisStyle = false;
            this.isDisabledAttribute = false;
        }

        /** @returns {boolean} -- true if axis empty/blank (no prompt or attribute) */
        isNullAxis() {
            return ! (this.attribute || this.isUsingPromptTitle);
        }

        /** @returns {boolean} -- true if axis has prompt (non-attribute title) */
        isPromptAxis() {
            return ( ! this.attribute && this.isUsingPromptTitle );
        }

        /** @returns {boolean} -- true if we have a non-disabled attribute to color by. */
        isColorAxis() {
            return Boolean( this.attribute && ! this.isDisabledAttribute ); // we have an attribute to color by
        }

        getColorAttribute() {
            return( this.isColorAxis() ? this.attribute : null );
        }

        isIncompleteAxis() {
            // return true if we have prompt in the "incomplete plot" style
            // see also .setIncompleteAxis()
            return Boolean(this.isIncompleteAxisStyle /*&& this.isUsingPromptTitle && ( ! this.attribute )*/ );
        }

        setIncompleteAxis( wantIncompleteAxisStyle ) {
            //let wasShowingIncompleteAxisStyle = this.isIncompleteAxis();
            // allow style to be on only if prompt axis, no legend attribute
            this.isIncompleteAxisStyle = Boolean(wantIncompleteAxisStyle && ! this.attribute);
            if( this.isIncompleteAxisStyle ) {
                this.isUsingPromptTitle = true; // also show prompt
                Dyn.assert( ! this.attribute ); // conditions for this.isIncompleteAxis()
            }
            // if( this.isIncompleteAxisStyle !== wasShowingIncompleteAxisStyle ) {
            //     // refresh legend if turned on or off
            //     this.updateVisuals();
            // }
        }

        /**
         * Set the attribute used for the legend, and update the legend display.
         * @param attribute
         */
        setSelectedAttributeLegend( attribute ) {
            if( attribute ) {
                this.attribute = attribute;
            } else {
                this.attribute = null;
            }
            this.updateLegend();
        }

        /** Update the Attribute Legend, to be done when ever the attribute changes, is cleared, or has data/filter changes */
        updateLegend() {
            // Remove element and tooltips, recursively if element is a set
            function removeElementAndToolTip( e ) {
                if( e.type==="set" ) {
                    $.each( e, function( index, element ) {
                        removeElementAndToolTip( element );
                    });
                    e.clear();
                } else {
                    if( Dyn.toolTip.isMyElement( e )) {
                        Dyn.toolTip.hide();
                    }
                    e.remove();
                }
            }

            // remove the old legend display
            if( this.attributeLegend ) {
                removeElementAndToolTip( this.attributeLegend );
            }

            // add the new legend display if any
            let attribute = this.attribute;
            if( attribute ) {
                let viewOnly = Dyn.preferences.isViewOnly(),
                    x = this.bounds.x,
                    y = this.bounds.y,
                    y1 = y + (this.spacer*2),
                    y2 = y + this.bounds.height,
                    x1 = x + this.spacer*2,
                    x2 = x + this.bounds.width - this.spacer,
                    titleX = this.bounds.centerX() + this.spacer * 2,
                    titleWidth = this.bounds.width - this.spacer * 3,
                    ariaLabel = Dyn.stringFormat(Dyn.plotUtil.legendCloseButtonAriaLabel, attribute.name);

                this.attributeLegend = this.paper.set();
                let titleStr = Dyn.plotUtil.unescapeText( attribute.name ),
                wantMathJaxStr = hasMathJaxPattern(titleStr);

                // show the attribute name as a title on center-top
                let attributeTitle = this.paper.text( titleX, y, titleStr ).attr({
                    "font-size": Dyn.preferences.getFontSize( this.legendFontSize ),
                    "font-weight": "bold",
                    'fill': Dyn.plotUtil.titleColor,
                    "class": 'tde-attribute-name' + (this.isUsingPromptTitle? ' ' + Dyn.plotUtil.hideOnPreviewClass: '') + (this.isDisabledAttribute? ' tde-disabled': '')
                });

                // apply tooltip for the attribute title on top of the legends
                Dyn.plotUtil.clipStringToWidth( attributeTitle, titleWidth );
                attributeTitle.attr({"transform": Dyn.plotUtil.getTextTransform( attributeTitle, "center,top" )});
                y1 += attributeTitle.getBBox().height;

                if(wantMathJaxStr) {
                    var bbox = attributeTitle.getBBox();
                    attributeTitle.remove();
                    attributeTitle = this.paper.g()
                    .attr({
                        "class": 'tde-attribute-name' + (this.isDisabledAttribute? ' tde-disabled': '')
                    });
                    attributeTitle.node.style.pointerEvents = "bounding-box";

                    const setSvgTitle = (_svg) => {
                        if(!attributeTitle.node) {
                            return;
                        }
                        this.svgCache = {
                            attrname: attribute.name,
                            svg: _svg
                        }
                        // Style svg
                        _svg.style.fontSize = Dyn.preferences.getFontSize( this.legendFontSize ) - 2;
                        _svg.style.fontWeight = "bold";
                        _svg.style.color = Dyn.plotUtil.titleColor;
                        // _svg.class = 'tde-attribute-name' + (this.isDisabledAttribute? ' tde-disabled': '')
                        // Append the svg into the title node
                        attributeTitle.node.appendChild(_svg);
                        // To position it in center of the give x and y position
                        attributeTitle.attr({
                            transform: 'r0T'+ Math.round(bbox.x) +',' + Math.round(bbox.y),
                            x: Math.round(bbox.x),
                            y: Math.round(bbox.y),
                            width: bbox.width,
                            height: bbox.height
                        });
                    }

                    // If svg cache is available and the attribute name is same, use the cached svg
                    // else create new SVG
                    if(this.svgCache && this.svgCache.attrname === attribute.name && this.svgCache.svg) {
                        setSvgTitle(this.svgCache.svg);
                    } else {
                        Dyn.axisUtil.mathJaxTxtToSVG(titleStr, setSvgTitle);
                    }
                }

                // Adding tooltip and hover events
                attributeTitle.data( { "tooltip": attribute.name });
                attributeTitle.hover(this.hoverStart, this.hoverEnd);
                this.attributeLegend.push( attributeTitle );

                // show the categorical or numeric data
                {
                    // Update the stats for Attribute
                    attribute.updateStatsIfDataChange();

                    let isUsingNumericIntervals = this.isUsingNumericIntervals && attribute.hasNumericIntervals(),
                        attributeLabels;

                    // Color Gradient or Color Squares
                    if (attribute.stats.isNumeric() && !isUsingNumericIntervals) {
                        attributeLabels = this.makeNumericGradient(attribute, x1, y1, x2, y2, isUsingNumericIntervals);
                    } else {
                        attributeLabels = this.makeCategoricalGradient(attribute, x1, y1, x2, y2, isUsingNumericIntervals);
                    }
                    this.attributeLegend.push( attributeLabels );
                }
                this.boundaryLine.show();
                this.dragLine.show();
                Dyn.plotUtil.showOrHideElement( this.closebox, !viewOnly );
                this.closebox[0].node.setAttribute( "aria-label", ariaLabel );
                Dyn.plotUtil.showOrHideElement( this.plusButton, this.isUsingPlusMinus && !viewOnly );
                Dyn.plotUtil.showOrHideElement( this.minusButton, this.isUsingPlusMinus && !viewOnly );

                this.isUsingPromptTitle = false;
                this.resetHighlightDropAreas();
            } else {
                // no selected attribute
                this.showHideBoundaryLine();
                this.dragLine.show();
                this.closebox.hide();
                this.plusButton.hide();
                this.minusButton.hide();
                this.scrollbar.hide();
                this.gradientOverlay.hide();
                this.marqueeSelectRect.hide();
            }
            this.needsLegendUpdate = false; // now up to date
        }

        /**
         * @param attribute {Dyn.Attribute}
         * @param x1
         * @param y1
         * @param x2
         * @param y2
         * @param isUsingNumericIntervals
         * @returns {*} - paper.set()
         */
        makeNumericGradient( attribute, x1, y1, x2, y2, isUsingNumericIntervals ) {
            var info = this.getCategoryInfo( attribute, isUsingNumericIntervals, Dyn.preferences.getFontSize( this.legendFontSize )),
                missingValueHeight = info.hasMissingValueCategory ? info.lineHeight : 0,
                largeGradientMargin = ((y2-y1 > 500) ? Dyn.plotUtil.dropExpandMin : 0 ), // don't go below x-axis if lots of space
                gradientWidth = Dyn.plotUtil.kGradientThickness,
                gradientX = x1 + Math.round( Math.max( this.spacer /*right justify*/, (x2-x1)*0.1 )),
                gradientY = y1,
                gradientHeight = Math.max(0, y2-y1 - this.spacer * 2 - missingValueHeight - largeGradientMargin),
                gradientRect = new Dyn.mathUtil.Rect( gradientX, gradientY, gradientWidth, gradientHeight ),
                tickX = gradientX + gradientWidth,
                tickX2 = tickX + this.legendNumericTickLength,
                tickXM2 = tickX + this.legendMinorTickLength,
                textX = tickX2 + this.legendNumericTickToTextSpace,
                attributeLabels,
                paper = this.paper,
                marginView = this;

            attributeLabels = this.paper.set();
            this.scrollbar.hide();
            if( this.isDisabledAttribute ) {
                // don't draw full numeric gradient, instead show grayedGradientRect
                let grayedGradientRect = this.paper.rect( gradientRect.x, gradientRect.y, gradientRect.width, gradientRect.height )
                    .attr( { fill: Dyn.plotUtil.kCaseMissingAxisColor, stroke:'none' });
                attributeLabels.push( grayedGradientRect );
                this.gradientOverlay.hide();
                this.marqueeSelectRect.hide();
                return attributeLabels;
            }

            // calc gradient and axis options
            let axisRange = new Dyn.mathUtil.Range( attribute.stats.min, attribute.stats.max ),
                axisOptions = {
                    subType: attribute.subType, // for special handling of date/time numeric types
                    formatString: attribute?.format?.formatString, // or fractions;
                    exactWidth: attribute?.format?.axisMajorWidth, // optional major interval width from MetaData
                    minorWidth: attribute?.format?.axisMinorWidth, // optional minor interval width from MetaData
                },
                targetNumTicks = Math.max( this.minNumTicks, Math.round( gradientHeight / this.pixelsPerTick )),
                isFraction = attribute.subType === Dyn.dataUtil.subtypes.kFraction;
            if( isFraction ) {
                targetNumTicks = Dyn.fractionData.approxNumTicksOnAxis;
                // fractions have tick marks 'outside' of data, to match axis tick marks
                axisOptions[Dyn.dataUtil.EIntervalRange.irLowerOutside] = true;
                axisOptions[Dyn.dataUtil.EIntervalRange.irUpperOutside] = true;
                axisOptions.maxDecimals = attribute.getNumericDecimals( undefined, true /* use data decimals only */ );
            } // else  Dyn.dataUtil.EIntervalRange.irLowerInside, irUpperInside
            axisRange.makeNonZeroWidth( 0.1 *(attribute.statsPreFilter.max - attribute.statsPreFilter.min));

            // make gradient
            let niceIntervals = Dyn.dataUtil.calcNiceIntervals( axisRange.min, axisRange.max, targetNumTicks, axisOptions ),
                coordMap = new Dyn.mathUtil.CoordMap( gradientY, gradientY+gradientHeight,
                    (attribute.isSortAscending ? axisRange.max : axisRange.min),
                    (attribute.isSortAscending ? axisRange.min : axisRange.max)),
                gradientSet;
            if( isFraction ) {
                // put data gradient inside extended axis ticks to go to next tick mark
                coordMap = new Dyn.mathUtil.CoordMap( gradientY, gradientY+gradientHeight,
                    (attribute.isSortAscending ? niceIntervals.max : niceIntervals.min),
                    (attribute.isSortAscending ? niceIntervals.min : niceIntervals.max));
                let yMinMax = [ axisRange.min, axisRange.max ].map( n => Math.round(coordMap.transform(n)))
                        .sort((a,b) => (a-b)), // y at location of min and max tickmarks, sorted in case data order is reversed
                    innerRect = new Dyn.mathUtil.Rect(
                        gradientRect.x, yMinMax[0], gradientRect.width, Math.max( 0, yMinMax[1] - yMinMax[0]) );
                gradientSet = Dyn.colorUtil.makeGradientSet( this.paper, innerRect, attribute,
                    false /* vertical */, false /* post-filter */, axisRange, gradientRect );
            } else {
                gradientSet = Dyn.colorUtil.makeGradientSet( this.paper, gradientRect, attribute,
                    false /* vertical */, false /* post-filter */, axisRange );
            }
            attributeLabels.push( gradientSet );

            // make labels
            let decimalPrecision = axisOptions.subType ? undefined : Dyn.mathUtil.decimalPlaces( niceIntervals.intervalWidth ),
                altFormatString = ( axisOptions.subType !== Dyn.dataUtil.subtypes.kFraction) ? undefined :
                    Dyn.fractionData.getAxisFormat( attribute.format.formatString, attribute.format.axisFormat ),
                minorTicks = Dyn.dataUtil.calcMinorTickData( coordMap, niceIntervals.minorIntervalWidths, niceIntervals.intervalWidth ),
                pixMinMax = (new Dyn.mathUtil.Range( gradientY, gradientY+gradientHeight+1 )).normalize();
            this.dragData.coordMap = coordMap;
            niceIntervals.values.forEach( dataVal => {
                let tickY = coordMap.transform( dataVal );
                if( pixMinMax.isInRange( tickY ) ) { // caution: niceIntervals calc may have one too many tickmarks
                    let textWidth = x2 - textX,
                        valueString = attribute.valueToString( dataVal, decimalPrecision, altFormatString ),
                        tickLine = paper.path( "M" + tickX + "," + tickY + "L" + tickX2 + "," + tickY ).attr( { stroke: Dyn.plotUtil.kAxisLineColor } ),
                        textElem = paper.text( textX, tickY, valueString );
                    if( this.showMinorTickMarks && minorTicks.hasMinor ) {
                        for( let m = 1; m < minorTicks.howMany; m++ ) { // ignore 0th tickmark which overlaps major
                            let tickYMinor = coordMap.transform( dataVal + (m * minorTicks.dataWidth) );
                            if( pixMinMax.isInRange( tickYMinor ) ) {
                                attributeLabels.push(
                                    paper.path( "M" + tickX + "," + tickYMinor + "L" + tickXM2 + "," + tickYMinor )
                                        .attr( { stroke: Dyn.plotUtil.kAxisLineColor } ) );
                            }
                        }
                    }
                    textElem.data( "tooltip", valueString ); // full text as tool tip in case tick label is clipped
                    textElem.hover( marginView.hoverStart, marginView.hoverEnd );
                    textElem.attr( {
                        "font-size": Dyn.preferences.getFontSize( this.legendFontSize ),
                        "text-anchor": "start",
                        'fill': Dyn.plotUtil.titleColor,
                        "class": 'tde-category-name'
                    } );
                    Dyn.plotUtil.clipStringToWidth( textElem, textWidth );
                    attributeLabels.push( tickLine, textElem );
                }
            });

            // missing value square
            if( info.hasMissingValueCategory ) {
                // category is {missing}
                info.catString = Dyn.dataUtil.kMissingValue; // normally a string, but use "null" since that is the actual data value
                info.displayString = Dyn.loc( Dyn.dataUtil.kMissingValueName );
                info.caseColor = new Dyn.colorUtil.SVG_Color( Dyn.plotUtil.kCaseMissingValueColor );
                // position at bottom of legend
                info.colorSquareX = gradientX;
                info.textX = info.colorSquareX + this.legendColorSquare + this.spacer;
                info.lineY = y2 - info.lineHeight;
                info.textWidth = x2 - info.textX;
                attributeLabels.push( this.createCategorySquareAndText( attribute, info, new Dyn.DragCategoryUtil(this) ) );
            }

            // numeric selection drag rect and visible indicator
            this.gradientOverlay.attr({ x:gradientRect.x, y:gradientRect.y, width:gradientRect.width, height:gradientRect.height })
                .show().toFront();
            let dataRange = Dyn.dataSet.getSelectedByNumericRange();
            if( dataRange ) {
                this.dragData.dataRange = new Dyn.mathUtil.Range( dataRange );
                let y1 = coordMap.transform( this.dragData.dataRange.min ),
                    y2 = coordMap.transform( this.dragData.dataRange.max );
                this.showMarqueeSelectRect( y1, y2 );
            } else {
                this.marqueeSelectRect.hide();
            }

            return attributeLabels;
        }

        /**
         * @param attribute {Dyn.Attribute}
         * @param x1
         * @param y1
         * @param x2
         * @param y2
         * @param isUsingNumericIntervals
         * @returns {*} - paper.set()
         */
        makeCategoricalGradient( attribute, x1, y1, x2, y2, isUsingNumericIntervals ) {
            "use strict";
            var info = this.getCategoryInfo( attribute, isUsingNumericIntervals ),
                attributeLabels = this.paper.set(),
                categories = info.categories,
                numCategories = info.numCategories;

            info.clipRectString = ''+x1+','+y1+','+(x2-x1)+','+(y2-y1);
            info.colorSquareX = x1;
            info.textX = x1 + this.legendColorSquare + this.spacer;
            info.lineY = y1;
            info.textWidth = x2 - info.textX;
            this.scrollbar.
                setViewRect( x1, y1, x2, y2 ).
                setWindowSize( info.lineHeight * numCategories, info.lineHeight ).
                updateView();
            info.textWidth -= this.scrollbar.getBarThickness();
            info.lineY -= this.scrollbar.getScrollOffset();
            this.gradientOverlay.hide(); // not used by categorical gradients
            this.marqueeSelectRect.hide(); // not used by categorical gradients
            let dragInfo = new Dyn.DragCategoryUtil( this, info.lineY, info.lineHeight, info.numDraggableCategories, attribute );

            // make color squares and labels for each category
            for( let i=0; i<numCategories; ++i ) {

                if( (info.lineY >= (y1 - info.lineHeight) ) && ( info.lineY < y2 )) { // in scroll area?
                    info.catIndex = i;
                    info.catString = categories[i];
                    info.displayString = (i<categories.length) ? attribute.categoryValueToString( info.catString ):"";
                    if( ! info.displayString ) {
                        info.catString = Dyn.dataUtil.kMissingValue; // null
                        info.displayString = Dyn.loc( Dyn.dataUtil.kMissingValueName ); // "{missing}"
                        info.caseColor = new Dyn.colorUtil.SVG_Color( Dyn.plotUtil.kCaseMissingValueColor);
                    } else if ( isUsingNumericIntervals ) {
                        info.caseColor = Dyn.colorUtil.calcContinuousColor(
                            attribute.statsPreFilter,
                            attribute.color,
                            attribute.numericIntervalIndexToValue( i ),
                            false );
                    } else {
                        info.caseColor = Dyn.colorUtil.calcCategoryColor(
                            attribute.statsPreFilter,
                            attribute.color,
                            info.catString,
                            false,
                            attribute.colorMap,
                            attribute.isSortAscending );
                    }
                    attributeLabels.push( this.createCategorySquareAndText( attribute, info, dragInfo ) );
                }

                info.lineY += info.lineHeight;
            }

            if( this.isDisabledAttribute ) {
                this.scrollbar.hide();
            } else {
                this.scrollbar.toFront(); // move scrollbar to top of newly-created elements (which default to front of paper when created)
            }
            return attributeLabels;
        }

        /**
         * Collect some information for creating a category gradient or single missing value color square.
         * @param attribute
         * @param isUsingNumericIntervals
         * @param optionalFontSize
         * @returns {{}}
         */
        getCategoryInfo( attribute, isUsingNumericIntervals, optionalFontSize ) {
            function getCategoryFontSize( numCategories ) {
                let stdSize = 12;
                if( numCategories <= 4 ) {
                    stdSize = 18;
                } else if( numCategories <= 8 ) {
                    stdSize = 16;
                } else if( numCategories <= 16 ) {
                    stdSize = 14;
                }
                return Dyn.preferences.getFontSize( stdSize );
            }

            var info = { };
            info.hasMissingValueCategory = ( attribute.stats.missingCaseCount > 0 );
            info.categories = ( isUsingNumericIntervals ? attribute.numericIntervals.categories : attribute.stats.categories);
            info.numCategories = info.categories.length + (info.hasMissingValueCategory ? 1 : 0);
            info.fontSize = optionalFontSize ? optionalFontSize : getCategoryFontSize( info.numCategories );
            info.lineHeight = Dyn.plotUtil.getTextBBox( this.paper, "X", info.fontSize ).height * Dyn.plotUtil.kLegendCategoryHeight;
            info.numDraggableCategories = info.categories.length; // exclude missing value category
            return info;
        }

        /**
         * Create a single line of the category legend, or the missing value line of the numeric legend
         * @param attribute
         * @param info -- all of the positioning and coloring information we require
         * @returns {*} --Raphael set with color square and category text label
         */
        createCategorySquareAndText( attribute, info, dragInfo ) {
            "use strict";
            let opacity = Dyn.dataSet.isDeselectedCategory( info.catString ) ? Dyn.plotUtil.kDeselectedCategoryOpacity : 1,
                set = this.paper.set();

            // category text
            let textElem = this.paper.text( info.textX, info.lineY, info.displayString );
            textElem.attr( {
                "font-size": info.fontSize,
                opacity: opacity,
                'clip-rect': info.clipRectString
            });
            if( ! this.isDisabledAttribute ) {
                textElem
                    .drag( this.dragMoveCategory, this.startDragCategory, this.endDragCategory )
                    //.click(this.clickCategory)
                    .hover(this.hoverStart, this.hoverEnd) // show tooltip on hover
                    .data("attributeID", attribute.id)
                    .data("category", info.catString)
                    .data("displayString", info.displayString )
                    .data("isNumericInterval", Boolean(this.isUsingNumericIntervals))
                    .data("tooltip", info.displayString)
                    .data("catIndex", info.catIndex )
                    .data("dragInfo", dragInfo ); // shared drag info object

            }
            Dyn.plotUtil.clipStringToWidth( textElem, info.textWidth );
            textElem.attr( {
                "transform": Dyn.plotUtil.getTextTransform( textElem, "left,top" ),
                'fill': Dyn.plotUtil.titleColor,
                "class": 'tde-category-name' + (this.isDisabledAttribute? ' tde-disabled': '')
            });
            set.push( textElem );

            // category color square
            let textBBox = textElem.getBBox();
            dragInfo.setSymbolOffset( textBBox.height / 2 - this.legendColorSquare / 2 ); // only changes when font size changes
            let colorSquare = this.paper.rect(info.colorSquareX, info.lineY + dragInfo.symbolOffset, this.legendColorSquare, this.legendColorSquare);
            this._calcBorderColor( info );
            colorSquare.attr(
                this.isDisabledAttribute
                    ? { fill: 'none', stroke: Dyn.plotUtil.kCaseMissingAxisColor }
                    : { fill: info.fillColor, stroke: info.borderColor } );

            if( ! this.isDisabledAttribute ){
                colorSquare
                    .drag( this.dragMoveCategory, this.startDragCategory, this.endDragCategory )
                    //.click(this.clickCategory)
                    .hover(this.hoverStart, this.hoverEnd) // show tooltip on hover
                    .data("attributeID", attribute.id)
                    .data("category", info.catString)
                    .data("displayString", info.displayString )
                    .data("isNumericInterval", Boolean(this.isUsingNumericIntervals))
                    .data("tooltip", info.displayString)
                    .data("catIndex", info.catIndex )
                    .data("dragInfo", dragInfo ); // shared drag info object
            }
            if (info.clipRectString) {
                colorSquare.attr('clip-rect', info.clipRectString);
            }
            set.push(colorSquare);

            return set;
        }

        _calcBorderColor( info ) {
            Dyn.colorUtil.calcColorBrightness( info.caseColor );
            let contrast255 = Math.abs(info.caseColor.b255 - Dyn.plotUtil.caseBackgroundBrightness);
            info.borderColor = ( contrast255 >= Dyn.plotUtil.caseBackgroundMinContrast )
                ? info.caseColor.colorString
                : Dyn.plotUtil.kCaseSubtleBorder;
            info.fillColor = info.caseColor.colorString;
        }

        /**
         * setup tooltip and any other relevant action
         */
        hoverStart( event ){
            "use strict";
            var isCategory = Boolean(this.data("category")),
                displayString = this.data("displayString"),
                isMissingElement = false;

            if (displayString === Dyn.loc(Dyn.dataUtil.kMissingValueName)){
                isMissingElement = true;
            }
            Dyn.toolTip.show( this, event, { x:this.getBBox().x, y:this.getBBox().y2, alignRight: true });
            if( isCategory || isMissingElement) {
                event.currentTarget.style.cursor = Dyn.plotUtil.cursorPointer;
            }
        }

        /**
         * Clean up the tooltip
         */
        hoverEnd( event ) {
            "use strict";
            if( Dyn.toolTip.isMyElement( this )) {
                Dyn.toolTip.hide();
            }
            event.currentTarget.style.cursor = Dyn.plotUtil.cursorDefault;
            //console.log("margin hoverEnd");
        }

        /**
         * Position and show the marquee select rectangle with a consistent margin for visibility.
         * When not in use, we just use .hide()
         */
        showMarqueeSelectRect( y1, y2 ) {
            let m = this.marqueeMargin,
                a = {
                    x: this.gradientOverlay.attrs.x - m,
                    y: Math.min(y1,y2) - m,
                    width: this.gradientOverlay.attrs.width + m + m,
                    height: Math.abs(y1-y2) + m + m
                };
            Dyn.assertValidRect( a );
            this.marqueeSelectRect.attr(a).show().toFront();
        }

        /** handler for clicking on Attribute Category */
        clickCategory( event ) {
            "use strict";
            var attrID = this.data("attributeID" ),
                categoryName = this.data("category" ),
                wantAdd = Boolean( event && event.shiftKey ),
                isNumericInterval = this.data("isNumericInterval" ),
                data = { "attributeID":attrID, "category":categoryName, addToSelection:wantAdd, isNumericInterval:isNumericInterval };
            // play the data value for this category (e.g. pie slice width as value for category 'red'
            if( categoryName ) {
                let plotView = Dyn.plotView;
                Dyn.sound.playOneCategoryValue( plotView, attrID, categoryName, isNumericInterval );
            }
            // select the cases with the given category for this attribute
            mediator.publish( "records:casesSelectedByCategory", data );
        }

        /** handler to drag one category (text or color square) in legend. */
        startDragCategory( x, y, event ) {
            // drag event callback, this===dragged element created by createCategorySquareAndText()
            let dragInfo = this.data('dragInfo'),
                isNonMissingValue = ! Dyn.dataUtil.isMissing( this.data("category"));
            if( dragInfo.isDraggable && // must be draggable categories (e.g. not intervals)
                isNonMissingValue ) { // must not be {missing} category with null string
                this.data('inDrag', true );
                this.data('y0', this.attr('y'));
                // change cursor while mouse down (then revert to pointer cursor)
                this.attr('cursor', Dyn.plotUtil.cursorVerticalBothSide );
            }
            Dyn.plotUtil.safeStopPropogation( event );
        }

        endDragCategory( event ) {
            // drag event callback, this===dragged element created by createCategorySquareAndText()
            let dragInfo = this.data('dragInfo');
            if( this.data('inDrag') ) {
                let oldY = this.data( 'y0' ),
                    newY = this.attr( 'y' ),
                    catIndex = this.data('catIndex'),
                    newIndex = this.data('newIndex'),
                    attributeID = this.data( 'attributeID' );
                if( dragInfo.isClickNotDrag( oldY, newY )) {
                    // no dragging took place on mouse up; treat as a click to select the category
                    dragInfo.view.clickCategory.call( this, event );
                }
                if( newIndex >= 0 && newIndex !== catIndex ) {
                    // animate to final position
                    dragInfo.view.moveCategoriesToDragPosition( oldY, catIndex, newIndex, dragInfo, true );
                    // save changes and update all views
                    setTimeout( ()=> {
                        Dyn.assert(attributeID);
                        mediator.publish( "moveCategoryOfAttribute", {
                            attributeID: attributeID,
                            oldIndex: catIndex,
                            newIndex: newIndex
                        })}, dragInfo.animationTimeMS ); // give time for animation to complete
                } else {
                    // drag ended in about the same position, redraw to original pos. to clean up view
                    dragInfo.view.moveCategoriesToDragPosition( oldY, catIndex, catIndex, dragInfo, true );
                }
                this.data( 'inDrag', false );
                // revert to pointer cursor, undoing startDragCategory() change
                this.attr('cursor', Dyn.plotUtil.cursorPointer );
            } else {
                // for not draggable categories like missing value, handle click
                dragInfo.view.clickCategory.call( this, event );
            }
            Dyn.plotUtil.safeStopPropogation( event );
        }

        dragMoveCategory( dx, dy, x, y, event ) {
            // drag event callback, this===dragged element created by createCategorySquareAndText()
            if( this.data('inDrag') ) {
                let dragInfo = this.data('dragInfo'),
                    newY = this.data('y0') + dy,
                    catIndex = this.data('catIndex'),
                    newIndex = dragInfo.getCategoryIndexFromDelta( catIndex, dy );
                this.data('newIndex', newIndex ); // save for dragEndCategory()
                dragInfo.view.moveCategoriesToDragPosition( newY, catIndex, newIndex, dragInfo );
                // maintain double-arrow cursor while dragging
                //event.currentTarget.style.cursor = Dyn.plotUtil.cursorVerticalBothSide;
            }
            Dyn.plotUtil.safeStopPropogation( event );
        }

        /** move the given attribute category to the new position,
         * and move the other categories in the legend out of the way.
         * @param v -- new position in Y of dragged category
         * @param oldIndex -- original (pre-drag) index of dragged category (in list of the attribute's categories)
         * @param newIndex -- new index of dragged category
         * @param dragInfo {{Dyn.DragCategoryUtil}} -- drag information (shared by all categories)
         * @param finalAnimation {boolean} -- true if we want dragged category to animate to new position
         */
        moveCategoriesToDragPosition( v, oldIndex, newIndex, dragInfo, finalAnimation ) {
            let catSets = this.attributeLegend[1]; // sets created by createCategorySquareAndText()
            catSets.forEach( (set ) => {
                // NOTE: with scroll bars, not all categories are in the set of visible elements. This is OK, we just
                // need to move the visible categories. Because of this, set index !== category index.
                let text = set[0],
                    square = set[1],
                    catIndex = text.data('catIndex');
                if( catIndex === oldIndex && ! finalAnimation ) {
                    // move visible category to the mouse position
                    v = dragInfo.getLimitedDragPosition( v );
                    text.attr( 'y', v );
                    square.attr('y', v+dragInfo.symbolOffset );
                } else {
                    // reposition other categories out of the way (leave a blank line where dragged category will go)
                    let ci2 = dragInfo.getCategoryIndexWithSkipping( catIndex, oldIndex, newIndex );
                    text.animate( { 'y': dragInfo.cat0Coord+(dragInfo.catOffset*ci2)
                        }, dragInfo.animationTimeMS, dragInfo.animationEasing );
                    square.animate( { 'y': dragInfo.cat0Coord+(dragInfo.catOffset*ci2)+dragInfo.symbolOffset
                        }, dragInfo.animationTimeMS, dragInfo.animationEasing );
                }
            });
        }

        handleCasesSelected( data ) {
            // hide marquee select if no longer selecting by numeric range
            Dyn.plotUtil.showOrHideElement( this.marqueeSelectRect, data.isNumericRangeChange );
        }

        /** handle an update to the legend attribute's data or metadata. */
        handleAttributeUpdate() {
            this.needsLegendUpdate = true;
        }

        setPlotSpecificOptions( options ) {
            var hadPlusMinus = this.isUsingPlusMinus,
                hadNumericIntervals = this.isUsingNumericIntervals,
                hadDisabledAttribute = this.isDisabledAttribute,
                hadIncompleteAxisStyle = this.isIncompleteAxisStyle;

            if( !options ) options = {};

            this.isUsingPlusMinus = Boolean(options.showPlusMinus);
            this.isUsingNumericIntervals = Boolean(options.useNumericIntervals);
            this.isDisabledAttribute = Boolean(options.disableAttribute);
            this.setIncompleteAxis(options.isIncompleteAxisStyle);

            // if any flags have changed then update the legend
            if( !this.needsLegendUpdate ){
                this.needsLegendUpdate = (
                    this.isUsingPlusMinus !== hadPlusMinus
                    || this.isUsingNumericIntervals !== hadNumericIntervals
                    || this.isDisabledAttribute !== hadDisabledAttribute);
            }
            if( !this.needsVisualsUpdate ){
                this.needsVisualsUpdate = (
                    this.isIncompleteAxisStyle !== hadIncompleteAxisStyle);
            }

            this.plusButton?.getCoverElement().data("isEnabled", ! options.disablePlusButton );
            this.minusButton?.getCoverElement().data("isEnabled", ! options.disableMinusButton );
        }

        /**
         * update our view after changes
         * from Dyn.PlotElement.CalcAxis() or Dyn.PlotShaper.updateAxes()
         */
        postCalcAxisUpdate() {
            // Update the legend if the attribute has changed, or the stats have changed
            // console.log("MarginViewView:postCalcAxisUpdate()");
            var legendStatsUpdated = this.getAttribute() && this.getAttribute().updateStatsIfDataChange(),
                legendAttributeNotChanged = ( this.getAttribute() === Dyn.dataSet.getSelectedAttribute());
            if( this.needsVisualsUpdate ) {
                this.updateVisuals(); // update everything; clears needsVisualsUpdate
            } else if( this.needsLegendUpdate || (legendStatsUpdated && legendAttributeNotChanged )) {
                // update attribute name and legend only; clears needsLegendUpdate
                this.setSelectedAttributeLegend( Dyn.dataSet.getSelectedAttribute() );
            }
        }

        /** is the given attribute used in the legend? */
        isAttributeInUse( attributeID ) {
            return this.attribute && this.attribute.id === attributeID;
        }

        showHideBoundaryLine() {
            let showLine = this.dragLine.data("inDrag") || this.dragLine.data("inHover") || Boolean(this.attribute);
            Dyn.plotUtil.showOrHideElement( this.boundaryLine, showLine );
        }

    } // class RightMarginView
    Dyn.RightMarginView = RightMarginView;
    //Dyn.RightMarginView.inheritsFrom( Dyn.MarginView );
    Dyn.RightMarginView.prototype.legendBorderColor = "#ccc";
    Dyn.RightMarginView.prototype.legendCoverColor = "#fff";
    Dyn.RightMarginView.prototype.legendCoverHighlightColor = "red";
    // Note: opacity 0.1 below combines with "red" above to make final pale-red highlight color,
    //       not really necessary but not worth changing for now --CDM 2018-11-13
    Dyn.RightMarginView.prototype.legendCoverOpacity = 0.1;
    Dyn.RightMarginView.prototype.marqueeMargin = 2;

}; // wrapDynMarginView
